import './App.css';
import AGI from './components/AGI';
import Header from './components/Header';
import MainContent from './components/MainContent';
import TimeMonitor from './components/TimeMonitor';
import ModalGeneric from './components/Modal/ModalGeneric';
import WelcomeBackModal from './components/Modal/WelcomeBackModal';

import AuthPage from './components/AuthPage'; // Import the AuthPage component

import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useSharedState } from './hooks/SharedStateContext';
import { PlannerProvider } from './hooks/PlannerContext';
import { syncLanguageChange } from './utilities/i18next'; // i18n initialized in index.js
import { getUrlParameter } from './utilities/urlUtils'; // Import the utility function
import { withPartnerSession } from './utilities/partnerSession';
import { authenticateUser, checkOnboardingStatus, hasNotificationPreferences } from './utilities/firebase'; // Import the new function
import ObjectiveView from './components/Activities/ObjectiveView';
import ReminderSetupCard from './components/Activities/ReminderSetupCard';
import {
  backfillReminderSetupEventIfNeeded,
  shouldShowReminderSetupPrompt,
  snoozeReminderSetupPrompt,
} from './utilities/notificationPrompt';

import Joyride from 'react-joyride';
import useJoyrideConfig from './utilities/joyrideConfig'; // Import the Joyride configuration hook

function App() {
  const [hasTimeLimitExceeded, setHasTimeLimitExceeded] = useState(false);
  const [isRealtime, setIsRealtime] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Add loading state until we get auth status
  const [showWelcomeBackModal, setShowWelcomeBackModal] = useState(false);
  const [showReminderSetupPrompt, setShowReminderSetupPrompt] = useState(false);
  const { sharedState, setSharedState, promptPlacementTestIfNeeded } = useSharedState();

  const { t } = useTranslation();
  const joyrideProps = useJoyrideConfig(); // Get the Joyride configuration

  useEffect(() => {
    // Check login status
    authenticateUser(async (authStatus) => {
      let onboardingStatus = null;
      if (authStatus) {
        // User is authenticated - check if they need onboarding
        // checkOnboardingStatus checks both Firebase AND localStorage
        onboardingStatus = await checkOnboardingStatus();

        if (onboardingStatus) {
          const { hasCompletedOnboarding, conversationCount } = onboardingStatus;

          // Users with 10+ conversations are considered experienced - skip onboarding
          // Users with <10 conversations who haven't completed onboarding → redirect
          if (!hasCompletedOnboarding && conversationCount < 10) {
            console.log('User needs onboarding:', { hasCompletedOnboarding, conversationCount });
            window.location.href = withPartnerSession('/onboarding');
            return;
          }
        }
      }

      setIsAuthenticated(authStatus);

      // Check if we should show the post-onboarding welcome modal
      const shouldShowWelcomeBack = localStorage.getItem('showWelcomeBackModal') === 'true';
      if (shouldShowWelcomeBack) {
        setShowWelcomeBackModal(true);
        localStorage.removeItem('showWelcomeBackModal');
      }

      // Wait for 1 second before setting isLoading to false
      setTimeout(() => {
        const shouldPromptPlacementTest = promptPlacementTestIfNeeded(null, {
          hasCompletedOnboarding: onboardingStatus?.hasCompletedOnboarding === true,
        });
        const hasPreferences = hasNotificationPreferences();
        if (authStatus) {
          backfillReminderSetupEventIfNeeded({ hasPreferences });
        }
        if (authStatus && !shouldShowWelcomeBack && shouldShowReminderSetupPrompt({
          hasPreferences,
          shouldPromptPlacementTest,
        })) {
          setShowReminderSetupPrompt(true);
        }
        setIsLoading(false);
      }, 1000);
    });
  }, []);

  useEffect(() => {
    if (
      !isLoading &&
      isAuthenticated &&
      !showWelcomeBackModal &&
      !showReminderSetupPrompt &&
      !sharedState.shouldPromptPlacementTest &&
      shouldShowReminderSetupPrompt({
        hasPreferences: hasNotificationPreferences(),
        shouldPromptPlacementTest: sharedState.shouldPromptPlacementTest,
      })
    ) {
      setShowReminderSetupPrompt(true);
    }
  }, [isLoading, isAuthenticated, showWelcomeBackModal, showReminderSetupPrompt, sharedState.shouldPromptPlacementTest]);

  useEffect(() => {
    // Force language synchronization after app loads
    const userLanguage = localStorage.getItem('userLanguage');
    console.log('🔧 App.js: Forcing language sync for userLanguage:', userLanguage);
    if (userLanguage) {
      setTimeout(() => {
        console.log('🔧 App.js: Syncing language to:', userLanguage);
        syncLanguageChange(userLanguage);
      }, 500); // Small delay to ensure i18next is ready
    }
  }, []);

  useEffect(() => {
    // Function to update CSS variables and determine mode based on URL parameters
    const updateCssVariablesAndMode = () => {
      const mode = getUrlParameter('mode');

      document.documentElement.style.setProperty('--background-purple', '#ffffff');

      // Determine if the mode is realtime
      if (mode === 'realtime') {
        setIsRealtime(true);
      } else {
        setIsRealtime(sharedState.isRealtime);
      }
    };

    updateCssVariablesAndMode();
  }, [sharedState.isRealtime]); // Empty dependency array ensures this runs once on component mount

  if (isLoading) {
    return (
      <div className="loader-container" role="status" aria-live="polite">
        <div className="loader" aria-hidden="true"></div>
        <span className="sr-only">{t('loading') || 'Loading...'}</span>
      </div>
    );
  }
  if (!isAuthenticated) {
    return <AuthPage onAuthSuccess={async () => {
      // Check for returnTo parameter (e.g., from OnboardingPage redirect)
      const urlParams = new URLSearchParams(window.location.search);
      const returnTo = urlParams.get('returnTo');

      // If explicitly requested via returnTo, redirect to that page
      if (returnTo === 'onboarding') {
        window.location.href = withPartnerSession('/onboarding');
        return;
      }
      if (returnTo === 'speech-profile') {
        window.location.href = '/speech-profile';
        return;
      }
      // Check if user needs onboarding (checks both Firebase AND localStorage)
      const onboardingStatus = await checkOnboardingStatus();

      if (onboardingStatus) {
        const { hasCompletedOnboarding, conversationCount } = onboardingStatus;

        // Users with 10+ conversations are considered experienced - skip onboarding
        // Users with <10 conversations who haven't completed onboarding → redirect
        if (!hasCompletedOnboarding && conversationCount < 10) {
          console.log('User needs onboarding after login:', { hasCompletedOnboarding, conversationCount });
          window.location.href = withPartnerSession('/onboarding');
          return;
        }
      }

      setIsAuthenticated(true);
    }} />;
  }

  return (
    <BrowserRouter>
      <PlannerProvider>
        <div className="App">
          {/* Skip to main content link for keyboard users (WCAG 2.4.1) */}
          <a href="#main-content" className="skip-link">
            {t('skip_to_main') || 'Skip to main content'}
          </a>

          <Header />
          <Joyride {...joyrideProps} run={sharedState.showTour}
            getHelpers={(helpers) => {
              helpers.go();
              helpers.open();
            }} />
          <TimeMonitor setHasTimeLimitExceeded={setHasTimeLimitExceeded} />
          <div className="container">
            <main id="main-content" aria-label={t('main_content_label') || 'Main content'}>
              <div className="main-child">
                <AGI />
              </div>
              <div className={`main-child${isRealtime ? ' realtime' : ''}`}>
                <Routes>
                  <Route path="/objective" element={<ObjectiveView />} />
                  <Route path="*" element={
                    <MainContent
                      isRealtime={isRealtime}
                      hasTimeLimitExceeded={hasTimeLimitExceeded}
                    />
                  } />
                </Routes>
              </div>
            </main>
          </div>

          {/* Welcome Back Modal - shown once after completing onboarding v2 */}
          <WelcomeBackModal
            isOpen={showWelcomeBackModal}
            onClose={() => setShowWelcomeBackModal(false)}
          />

          {/* Reminder setup prompt - shown on launch until reminders are configured; skip is session-only */}
          <ModalGeneric
            isOpen={showReminderSetupPrompt}
            onClose={() => {
              snoozeReminderSetupPrompt();
              setShowReminderSetupPrompt(false);
            }}
            title={t('reminder_prompt_title', 'Set up practice reminders')}
          >
            <p className="modal-description">
              {t('reminder_prompt_body', 'Want a nudge to keep practicing? Choose your days and time now.')}
            </p>
            <ReminderSetupCard
              source="app_launch_prompt"
              onSaved={() => setShowReminderSetupPrompt(false)}
            />
            <button
              type="button"
              className="modal-button secondary"
              onClick={() => {
                snoozeReminderSetupPrompt();
                setShowReminderSetupPrompt(false);
              }}
            >
              {t('remind_me_later', 'Remind me later')}
            </button>
          </ModalGeneric>

          {/* Test Results Modal - App level so it works in both realtime and non-realtime modes */}
          {sharedState.showTestResultsModal && (
            <ModalGeneric
              isOpen={sharedState.showTestResultsModal}
              onClose={() => setSharedState(prev => ({...prev, showTestResultsModal: false}))}
              title={t('test_results_title')}>
              <p>{t('test_results_body')}</p>
              <button className="modal-button" onClick={() => setSharedState(prev => ({...prev, showTestResultsModal: false}))}>
                {t('test_results_ok')}
              </button>
            </ModalGeneric>
          )}
        </div>
      </PlannerProvider>
    </BrowserRouter>
  );
}

export default App;
